import { useSyncExternalStore } from 'react';

/**
 * Where pop-ups appear on this device (Settings › Notifications). Kept in this
 * browser only, like the sound switch, and applied at once in every open tab.
 */

export const TOAST_PLACES = [
  { value: 'bottom-right', label: 'Bottom right' },
  { value: 'bottom-center', label: 'Bottom centre' },
  { value: 'bottom-left', label: 'Bottom left' },
  { value: 'top-right', label: 'Top right' },
  { value: 'top-center', label: 'Top centre' },
] as const;
export type ToastPlace = (typeof TOAST_PLACES)[number]['value'];

const KEY = '11deeds:toast-place';
const DEFAULT: ToastPlace = 'bottom-center';
const listeners = new Set<() => void>();

function read(): ToastPlace {
  try {
    const v = localStorage.getItem(KEY);
    return TOAST_PLACES.some((p) => p.value === v) ? (v as ToastPlace) : DEFAULT;
  } catch {
    return DEFAULT;
  }
}

export function setToastPlace(place: ToastPlace): void {
  try {
    localStorage.setItem(KEY, place);
  } catch {
    // private mode: this tab still follows it until reload
  }
  current = place;
  for (const l of listeners) l();
}

let current: ToastPlace | null = null;

function subscribe(l: () => void): () => void {
  listeners.add(l);
  const onStorage = (e: StorageEvent) => {
    if (e.key === KEY) {
      current = read();
      l();
    }
  };
  window.addEventListener('storage', onStorage);
  return () => {
    listeners.delete(l);
    window.removeEventListener('storage', onStorage);
  };
}

export function useToastPlace(): ToastPlace {
  return useSyncExternalStore(
    subscribe,
    () => (current ??= read()),
    () => DEFAULT,
  );
}
